<template>
    <b-modal :visible="Visible" @hide="onClose()" @close="onClose()" centered no-close-on-backdrop no-close-on-esc > 
        <template #modal-header="{close}">
            <b-row class="w-100 p-0 m-0 h-100 " align-v="center">
                <b-col class="text-left">
                    <h6 class="m-0">
                        {{ $t('FilterTitle', { filter: filterMode }) }}
                    </h6>
                </b-col>
                <b-col class=" pr-0 p-1 pl-4 text-right">                    
                    <b-icon role="button" icon="x-lg" font-scale="1.5" @click="onClose()" v-b-tooltip.hover.noninteractive :title="$t('Close')" />
                </b-col>
            </b-row>
        </template>

        <!-- Nom du regroupement/périmètre -->
        <b-form-group class="mb-2">
            <b-form-input v-model="name" :placeholder="$t('NameOf', { filter : filterMode })" />
        </b-form-group>

        <!-- Conservation des noeuds ancetres -->
        <b-form-group v-if="IsGrouping" class="mb-2" :style="generateVariantStyle(Variant)">
            <!-- Garder les ancêtres -->
            <b-form-checkbox switch v-model="keepAncestors">{{ $t('KeepAncestors') }}</b-form-checkbox>
        </b-form-group>  
        
        <!-- Remarque -->
        <b-form-group>
            <b-form-textarea v-model="remark" rows="3" max-rows="6" :placeholder="$tc('Remark', 1)" />
        </b-form-group>

        <template #modal-footer="{ cancel }">
            <b-button :variant="'outline-' + Variant" pill @click="cancel();">{{$t('Cancel')}}</b-button>
            <b-button :variant="Variant" pill :disabled="name.length == 0" @click="save(); cancel();">{{$t('Save1')}}</b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
    import { Component, InjectReactive, Model, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
    import FilterT from '@/types/FilterTree/FilterT';
    import Dictionary from '@/types/Dictionary';
    import axios from 'axios';
    import { API_URL } from '@/App.settings';
    import locales from "../locales/SaveFilter.json"
    import { ThemeUtils } from '../Utils';

    /** Fenêtre pour sauvegarder le filtre */
    @Component({
        components: {

        },
        i18n: {messages: locales}
    })
    export default class SaveFilter extends  ThemeUtils {
        /** Variant de couleur à appliquer sur le composant */
        @InjectReactive()
        public Variant!: string;

        /** @todo : mettre affichage/masquage modale par v-model dans une classe commune à hériter ? */
		//#region Affichage/masquage de la modale par v-model
        @Model('SaveFilterVisibleEvent', { default:  false }) /** type: boolean, */
        public Visible!: boolean;
        private visible: boolean = false;

		@Watch('Visible')
        private updateVisible() {
            this.visible = this.Visible;
        }

        private onClose() {
            this.visible = false;
            this.$emit('SaveFilterVisibleEvent', this.visible);
        }
        //#endregion Affichage/masquage de la modale par v-model

        //#region Saisie pour enregistrement du nom et autres
        private name: string = "";
        private remark: string = "";
        //#endregion Saisie pour enregistrement du nom et autres

        /** Permet juste d'afficher si on est sur du regroupement ou du périmètre */
        private filterMode = "";

		/** Nom du store Vuex persistant l'arborescence */
		@Prop()
		public FilterTreeStore!: string;

        /** Booleen permettant de savoir si on est en regroupement ou en périmètre */
        @Prop()
        public IsGrouping!: boolean;

        @PropSync('KeepAncestors', { default: () => false})
        public keepAncestors!: boolean;

        /** Enregistrement du filtre */
        private async save() {
            this.$store.dispatch("ShowLoadingMask", this.$t('LoadingFiltersInProgress')); // $t('LoadingFiltersInProgress') / Chargement des filtres en cours

            console.debug("SaveFilter - save() - name : %O", this.name);

            let filter: FilterT = this.$store.getters[this.FilterTreeStore + '/GetFilter'];

            if (!filter || Object.keys(filter).length == 0) {
                filter = new FilterT();
                filter.Name = this.name;
                filter.Remark = this.remark;
                filter.KeepAncestors = this.keepAncestors;                
            }
            else {
                filter.Name = this.name;
                filter.Remark = this.remark;
                filter.KeepAncestors = this.keepAncestors;
            }

            filter.Pins = this.$store.getters[this.FilterTreeStore + '/GetPins'];
            console.debug("saveFilter - save - this.pins : ", filter.Pins);

            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["FilterTree"] = JSON.stringify(filter);

            await axios.post<FilterT>(API_URL + "FilterTree/Save", args).then(async (response) => {
                if (response && response.data) {
                    let data: FilterT = response.data;
                    await this.$store.dispatch(this.FilterTreeStore + "/LoadFilters");
                    await this.$store.dispatch(this.FilterTreeStore + "/UpdateFilter", data);
                }
                this.$store.dispatch("HideLoadingMask", {});
            }).catch(err => {
                this.$store.dispatch("HideLoadingMask", {});
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccuredDuringWidgetLoading').toString(), { // $t('AnErrorOccuredDuringWidgetLoading') / Une erreur s'est produite sur lors du chargement de la bibliothèque de widget
                    title: this.$t('Error').toString(), // $t('Error') / Erreur
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            });
        }

        mounted(){
            let filter: FilterT = this.$store.getters[this.FilterTreeStore + '/GetFilter']
            console.debug("SaveFilter - mounted - filter :", filter);
            if (filter && Object.keys(filter).length > 0 ){
                this.name = filter.Name;
                this.remark = filter.Remark;
            }

            // Au chargement, on récupére Soit la valeur du filtre déjà enregistré Soit la valeur provenant de Grouping.vue
            this.keepAncestors = filter && Object.keys(filter).length > 0 ? filter.KeepAncestors : this.keepAncestors;

            this.filterMode = this.IsGrouping ? this.$t('Grouping').toString().toLowerCase() : this.$t('Perimeter').toString().toLowerCase();
        }
    }
</script>

<style scoped>
    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est coché */
    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
            border-color: var(--Variant) !important;
            background-color: var(--Variant) !important;
        }

    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est actif */
    :deep(.custom-control-input:active ~ .custom-control-label::before) {
        background-color: var(--Variant) !important;
        border-color: var(--Variant) !important;
    }
</style>